import {GenerateUuid} from '@shared/functions/uuid/generate-uuid';
import {UserEntity} from '@features/user/domain/entities/user.entity';
import {CustomerPersonTypeEnum} from '@features/customer/domain/enums/customer-person-type.enum';
import {CustomerValidationStatusTypeEnum} from '@features/customer/domain/enums/customer-validation-status-type.enum';

export class CustomerEntity {
  private readonly _id: string;
  private _users_phone_number: string;
  private _person_type_code: CustomerPersonTypeEnum;
  private _document_type_code: string;
  private _document_number: string;
  private _document_type_code_legal: string | null;
  private _document_number_legal: string | null;
  private _business_name: string | null;
  private _business_commercial_name: string | null;
  private _first_name: string;
  private _other_name: string;
  private _first_surname: string;
  private _second_surname: string;
  private _birth_date: string;
  private _expedition_date: string;
  private _expedition_country: string | null;
  private _expedition_state: string | null;
  private _expedition_city: string | null;
  private _image_front_document: string | null;
  private _image_front_document_ext: string | null;
  private _image_back_document: string | null;
  private _image_back_document_ext: string | null;
  private _selfie_user: string | null;
  private _selfie_user_ext: string | null;
  private _document_validate: boolean;
  private _biometric_2d_validate: boolean;
  private _biometric_3d_validate: boolean;
  private _addresses_id: string;
  private _police_validate: CustomerValidationStatusTypeEnum;
  private _comptroller_validate: CustomerValidationStatusTypeEnum;
  private _prosecutor_validate: CustomerValidationStatusTypeEnum;
  private _status_identity_document: CustomerValidationStatusTypeEnum;
  private _status_identity_document_back: CustomerValidationStatusTypeEnum;
  private _status_labor_certificate: CustomerValidationStatusTypeEnum;
  private _status_payroll_receipt: CustomerValidationStatusTypeEnum;
  private _status_bank_certificate: CustomerValidationStatusTypeEnum;
  private _image_police_validate: string | null;
  private _image_comptroller_validate: string | null;
  private _image_prosecutor_validate: string | null;
  private _identity_document: string | null;
  private _identity_document_back: string | null;
  private _labor_certificate: string | null;
  private _payroll_receipt: string | null;
  private _bank_certificate: string | null;
  private _income: number | null;
  private _expenses: number | null;
  private _legal_validation_notification: boolean;
  private _is_exits_pre_approved: boolean;
  private _agreement_id: string | null;
  private _user?: UserEntity;
  private readonly _createdAt: Date;
  private _updatedAt: Date;

  get id(): string {
    return this._id;
  }

  get users_phone_number(): string {
    return this._users_phone_number;
  }

  get person_type_code(): CustomerPersonTypeEnum {
    return this._person_type_code;
  }

  get document_type_code(): string {
    return this._document_type_code;
  }

  get document_number(): string {
    return this._document_number;
  }

  get document_type_code_legal(): string | null {
    return this._document_type_code_legal;
  }

  get document_number_legal(): string | null {
    return this._document_number_legal;
  }

  get business_name(): string | null {
    return this._business_name;
  }

  get business_commercial_name(): string | null {
    return this._business_commercial_name;
  }

  get first_name(): string {
    return this._first_name;
  }

  get other_name(): string {
    return this._other_name;
  }

  get first_surname(): string {
    return this._first_surname;
  }

  get second_surname(): string {
    return this._second_surname;
  }

  get birth_date(): string {
    return this._birth_date;
  }

  get expedition_date(): string {
    return this._expedition_date;
  }

  get expedition_country(): string | null {
    return this._expedition_country;
  }

  get expedition_state(): string | null {
    return this._expedition_state;
  }

  get expedition_city(): string | null {
    return this._expedition_city;
  }

  get image_front_document(): string | null {
    return this._image_front_document;
  }

  get image_front_document_ext(): string | null {
    return this._image_front_document_ext;
  }

  get image_back_document(): string | null {
    return this._image_back_document;
  }

  get image_back_document_ext(): string | null {
    return this._image_back_document_ext;
  }

  get selfie_user(): string | null {
    return this._selfie_user;
  }

  get selfie_user_ext(): string | null {
    return this._selfie_user_ext;
  }

  get document_validate(): boolean {
    return this._document_validate;
  }

  get biometric_2d_validate(): boolean {
    return this._biometric_2d_validate;
  }

  get biometric_3d_validate(): boolean {
    return this._biometric_3d_validate;
  }

  get addresses_id(): string {
    return this._addresses_id;
  }

  get police_validate(): CustomerValidationStatusTypeEnum {
    return this._police_validate;
  }

  get comptroller_validate(): CustomerValidationStatusTypeEnum {
    return this._comptroller_validate;
  }

  get prosecutor_validate(): CustomerValidationStatusTypeEnum {
    return this._prosecutor_validate;
  }

  get status_identity_document(): CustomerValidationStatusTypeEnum {
    return this._status_identity_document;
  }

  get status_identity_document_back(): CustomerValidationStatusTypeEnum {
    return this._status_identity_document_back;
  }

  get status_labor_certificate(): CustomerValidationStatusTypeEnum {
    return this._status_labor_certificate;
  }

  get status_payroll_receipt(): CustomerValidationStatusTypeEnum {
    return this._status_payroll_receipt;
  }

  get status_bank_certificate(): CustomerValidationStatusTypeEnum {
    return this._status_bank_certificate;
  }

  get image_police_validate(): string | null {
    return this._image_police_validate;
  }

  get image_comptroller_validate(): string | null {
    return this._image_comptroller_validate;
  }

  get image_prosecutor_validate(): string | null {
    return this._image_prosecutor_validate;
  }

  get identity_document(): string | null {
    return this._identity_document;
  }

  get identity_document_back(): string | null {
    return this._identity_document_back;
  }

  get labor_certificate(): string | null {
    return this._labor_certificate;
  }

  get payroll_receipt(): string | null {
    return this._payroll_receipt;
  }

  get bank_certificate(): string | null {
    return this._bank_certificate;
  }

  get income(): string | number | null {
    return this._income;
  }

  get expenses(): string | number | null {
    return this._expenses;
  }

  get legal_validation_notification(): boolean {
    return this._legal_validation_notification;
  }

  get is_exits_pre_approved(): boolean {
    return this._is_exits_pre_approved;
  }

  get agreement_id(): string | null {
    return this._agreement_id;
  }

  get user(): UserEntity | undefined {
    return this._user;
  }

  get createdAt(): Date {
    return this._createdAt;
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  protected constructor(props: CustomerRehydrateProps) {
    this._id = props.id ?? GenerateUuid();
    this._users_phone_number = props.users_phone_number;
    this._person_type_code = props.person_type_code;
    this._document_type_code = props.document_type_code;
    this._document_number = props.document_number;
    this._document_type_code_legal = props.document_type_code_legal;
    this._document_number_legal = props.document_number_legal;
    this._business_name = props.business_name;
    this._business_commercial_name = props.business_commercial_name;
    this._first_name = props.first_name;
    this._other_name = props.other_name;
    this._first_surname = props.first_surname;
    this._second_surname = props.second_surname;
    this._birth_date = props.birth_date;
    this._expedition_date = props.expedition_date;
    this._expedition_country = props.expedition_country;
    this._expedition_state = props.expedition_state;
    this._expedition_city = props.expedition_city;
    this._image_front_document = props.image_front_document;
    this._image_front_document_ext = props.image_front_document_ext;
    this._image_back_document = props.image_back_document;
    this._image_back_document_ext = props.image_back_document_ext;
    this._selfie_user = props.selfie_user;
    this._selfie_user_ext = props.selfie_user_ext;
    this._document_validate = props.document_validate;
    this._biometric_2d_validate = props.biometric_2d_validate;
    this._biometric_3d_validate = props.biometric_3d_validate;
    this._addresses_id = props.addresses_id;
    this._police_validate = props.police_validate;
    this._comptroller_validate = props.comptroller_validate;
    this._prosecutor_validate = props.prosecutor_validate;
    this._status_identity_document = props.status_identity_document;
    this._status_identity_document_back = props.status_identity_document_back;
    this._status_labor_certificate = props.status_labor_certificate;
    this._status_payroll_receipt = props.status_payroll_receipt;
    this._status_bank_certificate = props.status_bank_certificate;
    this._image_police_validate = props.image_police_validate;
    this._image_comptroller_validate = props.image_comptroller_validate;
    this._image_prosecutor_validate = props.image_prosecutor_validate;
    this._identity_document = props.identity_document;
    this._identity_document_back = props.identity_document_back;
    this._labor_certificate = props.labor_certificate;
    this._payroll_receipt = props.payroll_receipt;
    this._bank_certificate = props.bank_certificate;
    this._income = props.income;
    this._expenses = props.expenses;
    this._legal_validation_notification = props.legal_validation_notification;
    this._is_exits_pre_approved = props.is_exits_pre_approved;
    this._agreement_id = props.agreement_id;
    this._user = props.user;
    this._createdAt = props.createdAt;
    this._updatedAt = props.updatedAt;
  }

  static create(props: CustomerCreateProps): CustomerEntity {
    const now = new Date();
    return new CustomerEntity({...props, createdAt: now, updatedAt: now});
  }

  static rehydrate(props: CustomerRehydrateProps): CustomerEntity {
    return new CustomerEntity(props);
  }

  toPrimitives(): CustomerPrimitives {
    return {
      id: this._id,
      users_phone_number: this._users_phone_number,
      person_type_code: this._person_type_code,
      document_type_code: this._document_type_code,
      document_number: this._document_number,
      document_type_code_legal: this._document_type_code_legal,
      document_number_legal: this._document_number_legal,
      business_name: this._business_name,
      business_commercial_name: this._business_commercial_name,
      first_name: this._first_name,
      other_name: this._other_name,
      first_surname: this._first_surname,
      second_surname: this._second_surname,
      birth_date: this._birth_date,
      expedition_date: this._expedition_date,
      expedition_country: this._expedition_country,
      expedition_state: this._expedition_state,
      expedition_city: this._expedition_city,
      image_front_document: this._image_front_document,
      image_front_document_ext: this._image_front_document_ext,
      image_back_document: this._image_back_document,
      image_back_document_ext: this._image_back_document_ext,
      selfie_user: this._selfie_user,
      selfie_user_ext: this._selfie_user_ext,
      document_validate: this._document_validate,
      biometric_2d_validate: this._biometric_2d_validate,
      biometric_3d_validate: this._biometric_3d_validate,
      addresses_id: this._addresses_id,
      police_validate: this._police_validate,
      comptroller_validate: this._comptroller_validate,
      prosecutor_validate: this._prosecutor_validate,
      status_identity_document: this._status_identity_document,
      status_identity_document_back: this._status_identity_document_back,
      status_labor_certificate: this._status_labor_certificate,
      status_payroll_receipt: this._status_payroll_receipt,
      status_bank_certificate: this._status_bank_certificate,
      image_police_validate: this._image_police_validate,
      image_comptroller_validate: this._image_comptroller_validate,
      image_prosecutor_validate: this._image_prosecutor_validate,
      identity_document: this._identity_document,
      identity_document_back: this._identity_document_back,
      labor_certificate: this._labor_certificate,
      payroll_receipt: this._payroll_receipt,
      bank_certificate: this._bank_certificate,
      income: this._income,
      expenses: this._expenses,
      legal_validation_notification: this._legal_validation_notification,
      is_exits_pre_approved: this._is_exits_pre_approved,
      agreement_id: this._agreement_id,
      user: this._user,
      createdAt: this._createdAt.toISOString(),
      updatedAt: this._updatedAt.toISOString(),
    };
  }

  static fromPrimitives(props: CustomerPrimitives): CustomerEntity {
    return new CustomerEntity({
      ...props,
      createdAt: new Date(props.createdAt),
      updatedAt: new Date(props.updatedAt),
    });
  }

  public updateIncome(value: number): void {
    if (value >= 0) {
      this._income = value;
    } else {
      throw Error(`Cannot update income value for ${value}`);
    }
  }

  public updateExpense(value: number): void {
    if (value >= 0) {
      this._expenses = value;
    } else {
      throw Error(`Cannot update expenses value for ${value}`);
    }
  }

}

export interface CustomerCreateProps {
  users_phone_number: string;
  person_type_code: CustomerPersonTypeEnum;
  document_type_code: string;
  document_number: string;
  document_type_code_legal: string | null;
  document_number_legal: string | null;
  business_name: string | null;
  business_commercial_name: string | null;
  first_name: string;
  other_name: string;
  first_surname: string;
  second_surname: string;
  birth_date: string;
  expedition_date: string;
  expedition_country: string | null;
  expedition_state: string | null;
  expedition_city: string | null;
  image_front_document: string | null;
  image_front_document_ext: string | null;
  image_back_document: string | null;
  image_back_document_ext: string | null;
  selfie_user: string | null;
  selfie_user_ext: string | null;
  document_validate: boolean;
  biometric_2d_validate: boolean;
  biometric_3d_validate: boolean;
  addresses_id: string;
  police_validate: CustomerValidationStatusTypeEnum;
  comptroller_validate: CustomerValidationStatusTypeEnum;
  prosecutor_validate: CustomerValidationStatusTypeEnum;
  status_identity_document: CustomerValidationStatusTypeEnum;
  status_identity_document_back: CustomerValidationStatusTypeEnum;
  status_labor_certificate: CustomerValidationStatusTypeEnum;
  status_payroll_receipt: CustomerValidationStatusTypeEnum;
  status_bank_certificate: CustomerValidationStatusTypeEnum;
  image_police_validate: string | null;
  image_comptroller_validate: string | null;
  image_prosecutor_validate: string | null;
  identity_document: string | null;
  identity_document_back: string | null;
  labor_certificate: string | null;
  payroll_receipt: string | null;
  bank_certificate: string | null;
  income: number | null;
  expenses: number | null;
  legal_validation_notification: boolean;
  is_exits_pre_approved: boolean;
  agreement_id: string | null;
  user?: UserEntity;
}

export interface CustomerRehydrateProps extends CustomerCreateProps {
  id?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CustomerPrimitives extends CustomerCreateProps {
  id: string;
  createdAt: string;
  updatedAt: string;
}
